// src/pages/Solutions/EditSolutionPage.js
import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Paper, TextField, Button, Box, Grid,
  FormControl, InputLabel, Select, MenuItem, FormHelperText,
  CircularProgress, Alert, Chip, Divider, IconButton, GlobalStyles,
  Dialog, DialogContent, Slide, Checkbox,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { ArrowBack, Save, Add, Delete, CheckCircle, Launch } from '@mui/icons-material';
import api from '../../services/api';
import { useAppSelector } from '../../store/hooks';

const rengigsPalette = {
  forestGreen: '#2E7D32',
  deepForest: '#1B5E20',
  mediumGreen: '#4CAF50',
  lightGreen: '#8BC34A',
  brandLime: '#CDDC39',
  charcoal: '#1A2A3A',
  softGreen: 'rgba(46, 125, 50, 0.06)',
  white: '#FFFFFF',
  offWhite: '#F8F9FA',
  border: '#E1E9F0',
  gradient: 'linear-gradient(135deg, #2E7D32 0%, #4CAF50 40%, #8BC34A 70%, #CDDC39 100%)',
  gradientReverse: 'linear-gradient(135deg, #CDDC39 0%, #8BC34A 40%, #4CAF50 70%, #2E7D32 100%)',
};

const scrollbarStyles = {
  '*::-webkit-scrollbar': { width: '10px', height: '10px' },
  '*::-webkit-scrollbar-track': { background: rengigsPalette.offWhite, borderRadius: '10px' },
  '*::-webkit-scrollbar-thumb': { background: rengigsPalette.gradient, borderRadius: '10px' },
  '*::-webkit-scrollbar-thumb:hover': { background: rengigsPalette.gradientReverse },
  '*': { scrollbarWidth: 'thin', scrollbarColor: `${rengigsPalette.forestGreen} ${rengigsPalette.offWhite}` },
};

const validationSchema = yup.object({
  title:               yup.string().required('Title is required').max(255),
  tagline:             yup.string().max(255),
  description:         yup.string().required('Description is required').max(5000),
  solution_type:       yup.string().required('Solution type is required'),
  status:              yup.string().required('Status is required'),
  implementation_time: yup.string(),
  video_url:           yup.string(),
  api_documentation:   yup.string(),
  api_available:       yup.boolean(),
  training_available:  yup.boolean(),
  // SEO
  meta_title:          yup.string().max(60),
  meta_description:    yup.string().max(160),
  meta_keywords:       yup.string().max(255),
});

const SlideTransition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// ─── Section header helper ──────────────────────────────────────────────────
const SectionHeader = ({ label }) => (
  <Grid item xs={12}>
    <Typography variant="h6" sx={{ fontWeight: 800, color: rengigsPalette.charcoal, display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
      <Box sx={{ width: 4, height: 20, bgcolor: rengigsPalette.forestGreen, flexShrink: 0 }} />
      {label}
    </Typography>
    <Divider sx={{ mt: 1, mb: 3 }} />
  </Grid>
);

// ─── Dynamic list helper (features / benefits / integrations) ───────────────
const DynamicList = ({ label, items, onAdd, onRemove, onChange, placeholder, disabled }) => (
  <Grid item xs={12} md={6}>
    <Typography variant="h6" sx={{ fontWeight: 800, color: rengigsPalette.charcoal }}>{label}</Typography>
    <Divider sx={{ mb: 2 }} />
    {items.map((item, index) => (
      <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
        <TextField
          fullWidth size="small" value={item}
          placeholder={placeholder ? `${placeholder} ${index + 1}` : `Item ${index + 1}`}
          onChange={(e) => onChange(index, e.target.value)}
          disabled={disabled}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
        />
        <IconButton
          sx={{ color: rengigsPalette.forestGreen }}
          onClick={() => onRemove(index)}
          disabled={items.length === 1}
        >
          <Delete />
        </IconButton>
      </Box>
    ))}
    <Button
      startIcon={<Add />}
      onClick={onAdd}
      disabled={disabled}
      sx={{ color: rengigsPalette.forestGreen, fontWeight: 700, textTransform: 'none' }}
    >
      Add {label}
    </Button>
  </Grid>
);

// ─── Main component ─────────────────────────────────────────────────────────
const EditSolutionPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);

  const [loadingData, setLoadingData]         = useState(true);
  const [submitting, setSubmitting]           = useState(false);
  const [error, setError]                     = useState(null);
  const [fetchError, setFetchError]           = useState(null);
  const [success, setSuccess]                 = useState(false);
  const [solutionTitle, setSolutionTitle]     = useState('');

  const [categories, setCategories]           = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [loadingCategories, setLoadingCategories]   = useState(true);

  // Dynamic lists
  const [features, setFeatures]         = useState(['']);
  const [benefits, setBenefits]         = useState(['']);
  const [integrations, setIntegrations] = useState(['']);

  // Technical specs (key-value pairs)
  const [techSpecs, setTechSpecs] = useState([{ key: '', value: '' }]);

  // ── Fetch solution + categories ──────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        setLoadingData(true);

        const [solData, catData] = await Promise.all([
          api.get(`/solutions/${id}/`),
          api.get('/core/categories/'),
        ]);

        // ── Populate categories ──
        let catList = Array.isArray(catData) ? catData : (catData.results || []);
        catList = catList.filter(c => c.parent_id === null || c.parent_id === undefined || c.parent_id === '');
        setCategories(catList);
        setLoadingCategories(false);

        // ── Pre-select categories that the solution already has ──
        if (solData.categories?.length) {
          // categories on the solution may be objects {id, name} or just ids
          const solCatIds = new Set(
            solData.categories.map(c => (typeof c === 'object' ? c.id : c))
          );
          setSelectedCategories(catList.filter(c => solCatIds.has(c.id)));
        }

        // ── Dynamic lists ──
        setFeatures(solData.key_features?.length ? solData.key_features : ['']);
        setBenefits(solData.benefits?.length ? solData.benefits : ['']);
        setIntegrations(
          Array.isArray(solData.integrations) && solData.integrations.length
            ? solData.integrations
            : ['']
        );

        // ── Tech specs: object → array of {key, value} ──
        if (solData.technical_specs && typeof solData.technical_specs === 'object' && !Array.isArray(solData.technical_specs)) {
          const specEntries = Object.entries(solData.technical_specs);
          setTechSpecs(specEntries.length ? specEntries.map(([key, value]) => ({ key, value })) : [{ key: '', value: '' }]);
        }

        setSolutionTitle(solData.title);

        // ── Pre-fill formik ──
        formik.setValues({
          title:               solData.title               || '',
          tagline:             solData.tagline             || '',
          description:         solData.description         || '',
          solution_type:       solData.solution_type       || '',
          status:              solData.status              || 'draft',
          implementation_time: solData.implementation_time || '',
          video_url:           solData.video_url           || '',
          api_documentation:   solData.api_documentation   || '',
          api_available:       solData.api_available       ?? false,
          training_available:  solData.training_available  ?? false,
          meta_title:          solData.meta_title          || '',
          meta_description:    solData.meta_description    || '',
          meta_keywords:       solData.meta_keywords       || '',
        });

      } catch (err) {
        console.error('[EditSolutionPage] Failed to load solution:', err);
        setFetchError('Failed to load solution. It may not exist or you may not have access.');
      } finally {
        setLoadingData(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ── Formik ──────────────────────────────────────────────────────────────
  const formik = useFormik({
    initialValues: {
      title: '', tagline: '', description: '', solution_type: '',
      status: 'draft', implementation_time: '', video_url: '',
      api_documentation: '', api_available: false, training_available: false,
      meta_title: '', meta_description: '', meta_keywords: '',
    },
    validationSchema,
    enableReinitialize: false, // we manually set values after fetch
    onSubmit: async (values) => {
      try {
        setSubmitting(true);
        setError(null);

        // Build technical_specs back to object
        const technical_specs = {};
        techSpecs.forEach(({ key, value }) => {
          if (key.trim()) technical_specs[key.trim()] = value;
        });

        const payload = {
          title:               values.title,
          tagline:             values.tagline             || '',
          description:         values.description,
          solution_type:       values.solution_type,
          status:              values.status,
          implementation_time: values.implementation_time || '',
          video_url:           values.video_url?.trim()   || null,
          api_documentation:   values.api_documentation?.trim() || null,
          api_available:       values.api_available,
          training_available:  values.training_available,
          key_features:        features.filter(f => f.trim() !== ''),
          benefits:            benefits.filter(b => b.trim() !== ''),
          integrations:        integrations.filter(i => i.trim() !== ''),
          technical_specs,
          category_ids:        selectedCategories.map(c => c.id),
          // SEO
          meta_title:          values.meta_title       || '',
          meta_description:    values.meta_description || '',
          meta_keywords:       values.meta_keywords    || '',
        };

        await api.patch(`/solutions/${id}/`, payload);
        setSuccess(true);

      } catch (err) {
        console.error('[EditSolutionPage] PATCH error:', err);
        if (err.data && typeof err.data === 'object') {
          const messages = Object.entries(err.data)
            .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
            .join('\n');
          setError(messages || 'Failed to update solution');
        } else {
          setError(err.message || 'Failed to update solution');
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  // ── Dynamic list handlers ────────────────────────────────────────────────
  const makeListHandlers = (list, setList) => ({
    add:    ()          => setList([...list, '']),
    remove: (i)         => setList(list.length > 1 ? list.filter((_, idx) => idx !== i) : ['']),
    change: (i, val)    => { const n = [...list]; n[i] = val; setList(n); },
  });

  const feat = makeListHandlers(features, setFeatures);
  const ben  = makeListHandlers(benefits, setBenefits);
  const intg = makeListHandlers(integrations, setIntegrations);

  const handleSpecChange = (index, field, value) => {
    const updated = [...techSpecs];
    updated[index] = { ...updated[index], [field]: value };
    setTechSpecs(updated);
  };
  const addSpec    = ()  => setTechSpecs([...techSpecs, { key: '', value: '' }]);
  const removeSpec = (i) => setTechSpecs(techSpecs.length > 1 ? techSpecs.filter((_, idx) => idx !== i) : [{ key: '', value: '' }]);

  // ── Guards ───────────────────────────────────────────────────────────────
  if (loadingData) {
    return (
      <>
        <GlobalStyles styles={scrollbarStyles} />
        <Container sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: rengigsPalette.forestGreen }} />
        </Container>
      </>
    );
  }

  if (fetchError) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{fetchError}</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/solutions')}
          sx={{ color: rengigsPalette.charcoal, textTransform: 'none', fontWeight: 700 }}>
          Back to Solutions
        </Button>
      </Container>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      <GlobalStyles styles={scrollbarStyles} />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(`/solutions/${id}`)}
          sx={{ mb: 2, color: rengigsPalette.charcoal, fontWeight: 800, textTransform: 'none' }}>
          Back to Solution
        </Button>

        <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, borderRadius: 2, border: `1px solid ${rengigsPalette.border}` }}>

          {/* Page header */}
          <Box sx={{ mb: 4, p: 3, bgcolor: rengigsPalette.offWhite, borderRadius: 2, borderLeft: `6px solid ${rengigsPalette.forestGreen}` }}>
            <Typography variant="h4" sx={{ fontWeight: 900, color: rengigsPalette.charcoal, letterSpacing: '-0.02em' }}>
              Edit Solution
            </Typography>
            <Typography variant="body1" sx={{ color: '#64748B', fontWeight: 500 }}>
              {solutionTitle}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{error}</pre>
            </Alert>
          )}

          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={4}>

              {/* ── Core Details ──────────────────────────────────────────── */}
              <SectionHeader label="Core Details" />

              <Grid item xs={12} md={6}>
                <TextField fullWidth name="title" label="Solution Title *"
                  value={formik.values.title} onChange={formik.handleChange}
                  error={formik.touched.title && Boolean(formik.errors.title)}
                  helperText={formik.touched.title && formik.errors.title}
                  disabled={submitting}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={formik.touched.solution_type && Boolean(formik.errors.solution_type)}>
                  <InputLabel sx={{ color: rengigsPalette.forestGreen }}>Solution Type *</InputLabel>
                  <Select name="solution_type" value={formik.values.solution_type}
                    onChange={formik.handleChange} label="Solution Type *"
                    disabled={submitting} sx={{ borderRadius: '12px' }}>
                    <MenuItem value="product">Product</MenuItem>
                    <MenuItem value="service">Service</MenuItem>
                    <MenuItem value="software">Software</MenuItem>
                    <MenuItem value="hardware">Hardware</MenuItem>
                    <MenuItem value="consulting">Consulting</MenuItem>
                    <MenuItem value="platform">Platform</MenuItem>
                    <MenuItem value="integration">Integration</MenuItem>
                  </Select>
                  {formik.touched.solution_type && formik.errors.solution_type && (
                    <FormHelperText>{formik.errors.solution_type}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: rengigsPalette.forestGreen }}>Status</InputLabel>
                  <Select name="status" value={formik.values.status}
                    onChange={formik.handleChange} label="Status"
                    disabled={submitting} sx={{ borderRadius: '12px' }}>
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="published">Published</MenuItem>
                    <MenuItem value="under_review">Under Review</MenuItem>
                    <MenuItem value="archived">Archived</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Categories */}
              <Grid item xs={12} md={6}>
                {loadingCategories ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} sx={{ color: rengigsPalette.forestGreen }} />
                  </Box>
                ) : (
                  <FormControl fullWidth>
                    <InputLabel sx={{ color: rengigsPalette.forestGreen }}>Categories</InputLabel>
                    <Select
                      multiple
                      value={selectedCategories}
                      onChange={(e) => setSelectedCategories(e.target.value)}
                      label="Categories"
                      sx={{ borderRadius: '12px' }}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((cat) => (
                            <Chip key={cat.id} label={cat.name} size="small"
                              sx={{ bgcolor: rengigsPalette.forestGreen, color: '#FFF', fontWeight: 500 }} />
                          ))}
                        </Box>
                      )}
                      disabled={submitting}
                      MenuProps={{ PaperProps: { sx: { maxHeight: 400, width: 350 } } }}
                    >
                      {categories.length === 0 ? (
                        <MenuItem disabled>No categories available</MenuItem>
                      ) : (
                        categories.map((cat) => (
                          <MenuItem key={cat.id} value={cat} sx={{ py: 1.5, flexDirection: 'column', alignItems: 'flex-start' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                              <Checkbox
                                checked={selectedCategories.some(c => c.id === cat.id)}
                                sx={{ color: rengigsPalette.forestGreen, '&.Mui-checked': { color: rengigsPalette.forestGreen } }}
                              />
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: rengigsPalette.charcoal }}>
                                  {cat.name}
                                </Typography>
                                {cat.description && (
                                  <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', mt: 0.5 }}>
                                    {cat.description}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                )}
              </Grid>

              <Grid item xs={12}>
                <TextField fullWidth multiline rows={4} name="description"
                  label="Full Description *" value={formik.values.description}
                  onChange={formik.handleChange}
                  error={formik.touched.description && Boolean(formik.errors.description)}
                  helperText={formik.touched.description && formik.errors.description}
                  disabled={submitting}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField fullWidth name="tagline" label="Tagline / Short Description"
                  value={formik.values.tagline} onChange={formik.handleChange}
                  disabled={submitting}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>

              {/* ── Features & Benefits ───────────────────────────────────── */}
              <SectionHeader label="Features & Benefits" />

              <DynamicList label="Key Features" items={features} placeholder="Feature"
                onAdd={feat.add} onRemove={feat.remove} onChange={feat.change} disabled={submitting} />

              <DynamicList label="Benefits" items={benefits} placeholder="Benefit"
                onAdd={ben.add} onRemove={ben.remove} onChange={ben.change} disabled={submitting} />

              {/* ── Integrations ──────────────────────────────────────────── */}
              <SectionHeader label="Integrations" />

              <DynamicList label="Integrations" items={integrations} placeholder="Integration"
                onAdd={intg.add} onRemove={intg.remove} onChange={intg.change} disabled={submitting} />

              {/* ── Technical Specs ───────────────────────────────────────── */}
              <SectionHeader label="Technical Specifications" />

              <Grid item xs={12}>
                {techSpecs.map((spec, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 1.5, mb: 1.5, alignItems: 'center' }}>
                    <TextField
                      size="small" label="Spec Name" value={spec.key}
                      onChange={(e) => handleSpecChange(index, 'key', e.target.value)}
                      disabled={submitting} placeholder="e.g. OS Support"
                      sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    />
                    <TextField
                      size="small" label="Value" value={spec.value}
                      onChange={(e) => handleSpecChange(index, 'value', e.target.value)}
                      disabled={submitting} placeholder="e.g. Windows, Linux"
                      sx={{ flex: 2, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    />
                    <IconButton onClick={() => removeSpec(index)} disabled={techSpecs.length === 1}
                      sx={{ color: rengigsPalette.forestGreen }}>
                      <Delete />
                    </IconButton>
                  </Box>
                ))}
                <Button startIcon={<Add />} onClick={addSpec} disabled={submitting}
                  sx={{ color: rengigsPalette.forestGreen, fontWeight: 700, textTransform: 'none' }}>
                  Add Spec
                </Button>
              </Grid>

              {/* ── Implementation & Support ──────────────────────────────── */}
              <SectionHeader label="Implementation & Support" />

              <Grid item xs={12} md={6}>
                <TextField fullWidth name="implementation_time" label="Implementation Time"
                  placeholder="e.g., 2 weeks, 1 month"
                  value={formik.values.implementation_time} onChange={formik.handleChange}
                  disabled={submitting}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: rengigsPalette.forestGreen }}>API Availability</InputLabel>
                  <Select name="api_available" value={formik.values.api_available}
                    onChange={formik.handleChange} label="API Availability"
                    disabled={submitting} sx={{ borderRadius: '12px' }}>
                    <MenuItem value={true}>API Available</MenuItem>
                    <MenuItem value={false}>No API</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: rengigsPalette.forestGreen }}>Training</InputLabel>
                  <Select name="training_available" value={formik.values.training_available}
                    onChange={formik.handleChange} label="Training"
                    disabled={submitting} sx={{ borderRadius: '12px' }}>
                    <MenuItem value={true}>Training Available</MenuItem>
                    <MenuItem value={false}>No Training</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* ── Media & Links ─────────────────────────────────────────── */}
              <SectionHeader label="Media & Links" />

              <Grid item xs={12} md={6}>
                <TextField fullWidth name="video_url" label="Demo Video URL"
                  placeholder="https://..."
                  value={formik.values.video_url} onChange={formik.handleChange}
                  disabled={submitting}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField fullWidth name="api_documentation" label="API Documentation URL"
                  placeholder="https://..."
                  value={formik.values.api_documentation} onChange={formik.handleChange}
                  disabled={submitting}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>

              {/* ── SEO ───────────────────────────────────────────────────── */}
              <SectionHeader label="SEO" />

              <Grid item xs={12} md={6}>
                <TextField fullWidth name="meta_title" label="Meta Title"
                  value={formik.values.meta_title} onChange={formik.handleChange}
                  disabled={submitting}
                  inputProps={{ maxLength: 60 }}
                  helperText={`${formik.values.meta_title.length}/60 characters`}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField fullWidth name="meta_keywords" label="Meta Keywords"
                  placeholder="comma-separated keywords"
                  value={formik.values.meta_keywords} onChange={formik.handleChange}
                  disabled={submitting}
                  inputProps={{ maxLength: 255 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField fullWidth name="meta_description" label="Meta Description"
                  multiline rows={2}
                  value={formik.values.meta_description} onChange={formik.handleChange}
                  disabled={submitting}
                  inputProps={{ maxLength: 160 }}
                  helperText={`${formik.values.meta_description.length}/160 characters`}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>

              {/* ── Actions ───────────────────────────────────────────────── */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4, pt: 3, borderTop: `1px solid ${rengigsPalette.border}` }}>
                  <Button variant="outlined" onClick={() => navigate(`/solutions/${id}`)}
                    disabled={submitting}
                    sx={{
                      borderColor: rengigsPalette.forestGreen, color: rengigsPalette.forestGreen,
                      px: 4, py: 1.2, borderRadius: '12px', fontWeight: 600, textTransform: 'none',
                      '&:hover': { borderColor: rengigsPalette.brandLime, bgcolor: rengigsPalette.softGreen },
                    }}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="contained" disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <Save />}
                    sx={{
                      background: rengigsPalette.gradient, backgroundSize: '200% auto',
                      color: rengigsPalette.white, px: 6, py: 1.2, borderRadius: '12px',
                      fontWeight: 700, textTransform: 'none', transition: 'all 0.3s ease',
                      '&:hover': {
                        background: rengigsPalette.gradientReverse,
                        transform: 'translateY(-2px)',
                        boxShadow: `0 10px 20px -5px ${rengigsPalette.forestGreen}80`,
                      },
                      '&:disabled': { background: rengigsPalette.lightGreen, color: rengigsPalette.white },
                    }}>
                    {submitting ? 'Saving…' : 'Save Changes'}
                  </Button>
                </Box>
              </Grid>

            </Grid>
          </form>
        </Paper>
      </Container>

      {/* ── Success Dialog ─────────────────────────────────────────────────── */}
      <Dialog open={success} onClose={() => { setSuccess(false); navigate(`/solutions/${id}`); }}
        TransitionComponent={SlideTransition} maxWidth="sm" fullWidth
        PaperProps={{
          sx: {
            background: rengigsPalette.gradient, borderRadius: '16px',
            boxShadow: '0 20px 40px rgba(46,125,50,0.3)', m: 2,
          }
        }}>
        <DialogContent sx={{ p: 4, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: 80, height: 80, borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1,
              animation: 'pulse 0.5s ease-in-out',
              '@keyframes pulse': {
                '0%': { transform: 'scale(0.8)', opacity: 0 },
                '50%': { transform: 'scale(1.1)' },
                '100%': { transform: 'scale(1)', opacity: 1 },
              },
            }}>
              <CheckCircle sx={{ fontSize: 48, color: rengigsPalette.white }} />
            </Box>

            <Typography variant="h5" sx={{ fontWeight: 700, color: rengigsPalette.white }}>
              Solution Updated!
            </Typography>
            <Typography variant="body2" sx={{ color: rengigsPalette.white, opacity: 0.85 }}>
              Your changes have been saved successfully.
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, width: '100%', mt: 2 }}>
              <Button fullWidth variant="outlined" onClick={() => { setSuccess(false); navigate(`/solutions/${id}`); }}
                endIcon={<Launch sx={{ fontSize: 18 }} />}
                sx={{
                  borderColor: rengigsPalette.white, color: rengigsPalette.white,
                  py: 1.2, borderRadius: '10px', fontWeight: 600, textTransform: 'none',
                  '&:hover': { borderColor: rengigsPalette.white, backgroundColor: 'rgba(255,255,255,0.1)' },
                }}>
                View Solution
              </Button>
              <Button fullWidth variant="contained"
                onClick={() => { setSuccess(false); navigate('/solutions'); }}
                sx={{
                  backgroundColor: rengigsPalette.white, color: rengigsPalette.forestGreen,
                  py: 1.2, borderRadius: '10px', fontWeight: 600, textTransform: 'none',
                  '&:hover': { backgroundColor: rengigsPalette.white, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' },
                }}>
                All Solutions
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EditSolutionPage;